import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  QrCodeIcon,
  DocumentTextIcon,
  CreditCardIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  UserIcon,
  CalendarIcon,
  BanknotesIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline'
import { hubnaiAPI } from '@/services/api'
import type { HubnaiCharge } from '@/services/api'

const GatewayFinProChargeDetails: React.FC = () => {
  const { chargeId } = useParams<{ chargeId: string }>()
  const navigate = useNavigate()

  const [charge, setCharge] = useState<HubnaiCharge | null>(null)
  const [loading, setLoading] = useState(true)
  const [cancelling, setCancelling] = useState(false)
  const [refunding, setRefunding] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    if (chargeId) {
      loadCharge()
    }
  }, [chargeId])

  const loadCharge = async () => {
    try {
      setLoading(true)
      const data = await hubnaiAPI.getCharge(parseInt(chargeId!))
      setCharge(data)
    } catch (error: any) {
      console.error('Erro ao carregar cobrança:', error)
      alert('Erro ao carregar cobrança: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!confirm('Tem certeza que deseja cancelar esta cobrança?')) return

    try {
      setCancelling(true)
      await hubnaiAPI.cancelCharge(parseInt(chargeId!))
      await loadCharge()
      alert('Cobrança cancelada com sucesso!')
    } catch (error: any) {
      console.error('Erro ao cancelar:', error)
      alert('Erro ao cancelar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setCancelling(false)
    }
  }

  const handleRefund = async () => {
    const reason = prompt('Motivo do estorno:')
    if (!reason) return

    try {
      setRefunding(true)
      await hubnaiAPI.refundCharge(parseInt(chargeId!), { reason })
      await loadCharge()
      alert('Estorno realizado com sucesso!')
    } catch (error: any) {
      console.error('Erro ao estornar:', error)
      alert('Erro ao estornar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setRefunding(false)
    }
  }

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopied(field)
    setTimeout(() => setCopied(null), 2000)
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusConfig = (status: string) => {
    const configs = {
      pending: {
        label: 'Pendente',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: ClockIcon,
        iconColor: 'text-yellow-600'
      },
      paid: {
        label: 'Pago',
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: CheckCircleIcon,
        iconColor: 'text-green-600'
      },
      failed: {
        label: 'Falhou',
        color: 'bg-red-100 text-red-800 border-red-300',
        icon: XCircleIcon,
        iconColor: 'text-red-600'
      },
      cancelled: {
        label: 'Cancelado',
        color: 'bg-gray-100 text-gray-800 border-gray-300',
        icon: ExclamationCircleIcon,
        iconColor: 'text-gray-600'
      },
      expired: {
        label: 'Expirado',
        color: 'bg-orange-100 text-orange-800 border-orange-300',
        icon: ClockIcon,
        iconColor: 'text-orange-600'
      },
      refunded: {
        label: 'Estornado',
        color: 'bg-purple-100 text-purple-800 border-purple-300',
        icon: ShieldExclamationIcon,
        iconColor: 'text-purple-600'
      }
    }

    return configs[status as keyof typeof configs] || configs.pending
  }

  const getPaymentMethodConfig = (method: string) => {
    const configs = {
      pix: {
        label: 'PIX',
        icon: QrCodeIcon,
        color: 'text-purple-600 bg-purple-100'
      },
      boleto: {
        label: 'Boleto',
        icon: DocumentTextIcon,
        color: 'text-orange-600 bg-orange-100'
      },
      credit_card: {
        label: 'Cartão de Crédito',
        icon: CreditCardIcon,
        color: 'text-blue-600 bg-blue-100'
      },
      debit_card: {
        label: 'Cartão de Débito',
        icon: CreditCardIcon,
        color: 'text-green-600 bg-green-100'
      }
    }

    return configs[method as keyof typeof configs] || configs.pix
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!charge) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationCircleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Cobrança não encontrada</h2>
          <button
            onClick={() => navigate('/payments/gateway-finpro')}
            className="text-blue-600 hover:text-blue-700"
          >
            Voltar para Gateway FinPro
          </button>
        </div>
      </div>
    )
  }

  const statusConfig = getStatusConfig(charge.status)
  const paymentConfig = getPaymentMethodConfig(charge.payment_method)
  const StatusIcon = statusConfig.icon
  const PaymentIcon = paymentConfig.icon

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/payments/gateway-finpro')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Detalhes da Cobrança</h1>
              <p className="text-sm text-gray-500">ID: {charge.hubnai_charge_id}</p>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${statusConfig.color}`}>
              <StatusIcon className={`w-5 h-5 ${statusConfig.iconColor}`} />
              <span className="font-semibold">{statusConfig.label}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            {charge.status === 'pending' && (
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {cancelling ? 'Cancelando...' : 'Cancelar Cobrança'}
              </button>
            )}
            {charge.status === 'paid' && (
              <button
                onClick={handleRefund}
                disabled={refunding}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {refunding ? 'Estornando...' : 'Estornar Pagamento'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Valor */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações Financeiras</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Valor da Cobrança</span>
                <span className="text-2xl font-bold text-gray-900">{formatCurrency(charge.amount)}</span>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${paymentConfig.color}`}>
                    <PaymentIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Método de Pagamento</p>
                    <p className="font-semibold text-gray-900">{paymentConfig.label}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PIX QR Code */}
          {charge.payment_method === 'pix' && charge.pix_qr_code_url && charge.status === 'pending' && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">QR Code PIX</h3>
              <div className="flex flex-col items-center">
                <div className="bg-white p-4 rounded-lg border-2 border-purple-500">
                  <img
                    src={charge.pix_qr_code_url}
                    alt="QR Code PIX"
                    className="w-64 h-64"
                  />
                </div>
                <p className="text-sm text-gray-600 mt-4 text-center">
                  Escaneie o QR Code para pagar
                </p>
              </div>

              {charge.pix_code && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código PIX Copia e Cola
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={charge.pix_code}
                      readOnly
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono bg-gray-50"
                    />
                    <button
                      onClick={() => copyToClipboard(charge.pix_code!, 'pix')}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                    >
                      {copied === 'pix' ? (
                        <>
                          <CheckIcon className="w-5 h-5" />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <ClipboardDocumentIcon className="w-5 h-5" />
                          Copiar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Boleto */}
          {charge.payment_method === 'boleto' && charge.boleto_url && (
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Boleto Bancário</h3>
              <div className="space-y-4">
                <a
                  href={charge.boleto_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                  <DocumentTextIcon className="w-5 h-5" />
                  Abrir Boleto PDF
                </a>

                {charge.due_date && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-orange-800">
                      <CalendarIcon className="w-5 h-5" />
                      <div>
                        <p className="text-sm font-medium">Vencimento</p>
                        <p className="font-semibold">{formatDate(charge.due_date)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Datas */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Criada em</p>
                  <p className="font-medium text-gray-900">{formatDate(charge.created_at)}</p>
                </div>
              </div>

              {charge.expires_at && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Expira em</p>
                    <p className="font-medium text-gray-900">{formatDate(charge.expires_at)}</p>
                  </div>
                </div>
              )}

              {charge.payment_date && (
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-600">Pago em</p>
                    <p className="font-medium text-gray-900">{formatDate(charge.payment_date)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Cliente */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <UserIcon className="w-5 h-5" />
              Cliente
            </h3>
            {charge.customer_name || charge.customer_email ? (
              <div className="space-y-3">
                {charge.customer_name && (
                  <div>
                    <p className="text-sm text-gray-600">Nome</p>
                    <p className="font-medium text-gray-900">{charge.customer_name}</p>
                  </div>
                )}
                {charge.customer_email && (
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium text-gray-900">{charge.customer_email}</p>
                  </div>
                )}
                {charge.customer_document && (
                  <div>
                    <p className="text-sm text-gray-600">CPF/CNPJ</p>
                    <p className="font-medium text-gray-900">{charge.customer_document}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">Cobrança avulsa sem cliente cadastrado</p>
              </div>
            )}
          </div>

          {/* Estatísticas Rápidas */}
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <BanknotesIcon className="w-6 h-6" />
              <h3 className="text-lg font-semibold">Resumo</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-blue-100">ID Cobrança</span>
                <span className="font-mono text-sm">{charge.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-100">Status</span>
                <span className="font-semibold">{statusConfig.label}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-blue-100">Método</span>
                <span className="font-semibold">{paymentConfig.label}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GatewayFinProChargeDetails
